import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ENavigationPath } from 'constants/path';
import { Button, message } from 'antd';
import { useNavigate } from 'react-router';
import { UWLogo } from 'assets/images/logo';
import { useTranslation } from 'react-i18next';
import ellipse01 from 'assets/images/ellipse-01.png';
import ellipse02 from 'assets/images/ellipse-02.png';
import ellipse03 from 'assets/images/ellipse-03.png';
import ellipse04 from 'assets/images/ellipse-04.png';
import loginIllustration from 'assets/images/login-illustration.png';
import {
  loginRedirectUri,
  loginRequest,
  shouldUseCentralizedRedirectUri,
} from 'services/msal/configs';
import { useAuth } from 'hooks/useAuth';

function Login() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ENavigationPath.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      await instance.loginRedirect({
        ...loginRequest,
        redirectUri: loginRedirectUri,
        state:
          shouldUseCentralizedRedirectUri &&
          loginRedirectUri !==
            `${window.location.origin}${ENavigationPath.AUTH_CALLBACK}`
            ? JSON.stringify({
                originalRedirectOrigin: `${window.location.origin}`,
              })
            : undefined,
      });
    } catch {
      message.error(t('login.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <img src={ellipse01} className="absolute top-0 left-20" />
      <img src={ellipse02} className="absolute top-0 left-0" />
      <img src={ellipse03} className="absolute bottom-0 right-10" />
      <img src={ellipse04} className="absolute bottom-0 right-28" />

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        <div className="flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-lg">
            <div className="flex flex-col items-center">
              <UWLogo />
              <h2 className="mt-6 text-3xl font-bold text-gray-900">
                {t('login.welcomeText')}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {t('login.actionText')}
              </p>
            </div>

            <Button
              type="primary"
              size="large"
              className="w-full h-12 rounded-lg"
              onClick={handleLogin}
              loading={isLoading}
            >
              {t('login.signInWithMicrosoft')}
            </Button>
          </div>
          <div className="absolute bottom-10 text-sm text-gray-500">
            {t('login.allRightsReserved', { year: new Date().getFullYear() })}
          </div>
        </div>

        <div className="hidden lg:flex flex-col items-center justify-center p-8 gap-8">
          <div className="relative">
            <img
              src={loginIllustration}
              className="max-h-[90vh] w-auto object-contain"
              alt="Login illustration"
            />
            <h1 className="absolute bottom-8 left-8 text-3xl font-semibold text-[#F6FCFF] text-left max-w-[60%]">
              {t('login.slogan')}
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
